const finiteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Les montants affichés et encaissés par la caisse sont exprimés au franc.
// Neutraliser ici les résidus IEEE-754 évite que 9 999,999999 et 10 000
// deviennent deux natures d'article différentes.
const normalizeMoney = (value) => Math.round(finiteNumber(value));
const normalizeRatio = (value) =>
  Math.round(finiteNumber(value) * 1_000_000_000) / 1_000_000_000;

export const getPersistentSplitItemId = (item) => {
  const direct =
    item?.order_item_id ?? item?.orderItemId ?? item?.order_itemId ?? null;
  if (direct !== null && direct !== undefined && String(direct).trim()) {
    return direct;
  }
  if (item?.order_id || item?.orderId) return item?.id ?? null;
  return null;
};

const normalizeExtrasKey = (extras) =>
  (Array.isArray(extras) ? extras : [])
    .map((extra) => ({
      id: extra?.id ?? extra?.extra_id ?? null,
      name: String(extra?.name ?? extra?.extra_name ?? ""),
      type: String(extra?.type ?? extra?.extra_type ?? ""),
      quantity: finiteNumber(extra?.quantity, 1),
      price: finiteNumber(extra?.price ?? extra?.unit_price),
    }))
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)))
    .map((extra) => JSON.stringify(extra))
    .join("|");

const normalizeNatureLabel = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("fr");

const normalizeNatureExtrasKey = (item) => {
  const lineQuantity = Math.max(1, finiteNumber(item?.quantity, 1));
  return (Array.isArray(item?.extras) ? item.extras : [])
    .map((extra) => ({
      name: normalizeNatureLabel(extra?.name ?? extra?.extra_name),
      type: normalizeNatureLabel(extra?.type ?? extra?.extra_type),
      quantityPerItem: normalizeRatio(
        finiteNumber(extra?.quantity, 1) / lineQuantity,
      ),
      unitPrice: normalizeMoney(extra?.price ?? extra?.unit_price),
    }))
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)))
    .map((extra) => JSON.stringify(extra))
    .join("|");
};

export const getSplitItemKey = (item) => {
  const persistentId = getPersistentSplitItemId(item);
  if (persistentId !== null && persistentId !== undefined) {
    return `order-item:${persistentId}`;
  }
  const clientLineId = item?.client_line_id ?? item?.clientLineId ?? null;
  if (clientLineId) return `draft:${clientLineId}`;
  return [
    "product",
    item?.product_id ?? item?.productId ?? item?.id ?? "",
    item?.variation_id ?? item?.variationId ?? "",
    String(item?.name ?? item?.product_name ?? ""),
    normalizeExtrasKey(item?.extras),
  ].join(":");
};

export const getSplitItemUnitTotal = (item) => {
  const stableUnitTotal = Number(item?.__splitUnitTotal);
  if (Number.isFinite(stableUnitTotal)) return normalizeMoney(stableUnitTotal);
  const quantity = Math.max(1, finiteNumber(item?.quantity, 1));
  const lineTotal = finiteNumber(item?.totalPrice ?? item?.total_price, NaN);
  if (Number.isFinite(lineTotal)) return normalizeMoney(lineTotal / quantity);
  const extras = (Array.isArray(item?.extras) ? item.extras : []).reduce(
    (sum, extra) =>
      sum +
      finiteNumber(extra?.price ?? extra?.unit_price) *
        Math.max(1, finiteNumber(extra?.quantity, 1)),
    0,
  );
  return normalizeMoney(
    finiteNumber(item?.unitPrice ?? item?.price ?? item?.unit_price) + extras,
  );
};

export const getSplitItemNatureKey = (item) => {
  if (item?.__splitNatureKey) return item.__splitNatureKey;
  return [
    "nature",
    normalizeNatureLabel(item?.name ?? item?.product_name),
    normalizeNatureLabel(item?.variation_name ?? item?.variationName) ||
      `variation:${item?.variation_id ?? item?.variationId ?? ""}`,
    getSplitItemUnitTotal(item),
    normalizeNatureExtrasKey(item),
  ].join(":");
};

const withQuantity = (item, quantity) => {
  // Figer l'identité visuelle et le prix unitaire avant de modifier la quantité.
  // Sans cela, une ligne avec suppléments changeait de clé en passant de 2 à 1,
  // puis réapparaissait ailleurs comme s'il s'agissait d'un autre article.
  const unitTotal = getSplitItemUnitTotal(item);
  const natureKey = getSplitItemNatureKey({
    ...item,
    __splitUnitTotal: unitTotal,
  });
  const nextQuantity = Math.max(0, finiteNumber(quantity));
  const total = unitTotal * nextQuantity;
  return {
    ...item,
    __splitUnitTotal: unitTotal,
    __splitNatureKey: natureKey,
    quantity: nextQuantity,
    totalPrice: total,
    total_price: total,
  };
};

export const mergeSplitEditorItems = (items = []) => {
  const merged = [];
  const indexByKey = new Map();
  for (const rawItem of Array.isArray(items) ? items : []) {
    const quantity = finiteNumber(rawItem?.quantity);
    if (quantity <= 0) continue;
    const item = withQuantity(rawItem, quantity);
    const key = getSplitItemKey(item);
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length);
      merged.push(item);
      continue;
    }
    const existing = merged[existingIndex];
    merged[existingIndex] = withQuantity(
      existing,
      finiteNumber(existing.quantity) + quantity,
    );
  }
  return merged;
};

const recalculateInvoice = (invoice, items) => {
  const normalizedItems = mergeSplitEditorItems(items);
  return {
    ...invoice,
    items: normalizedItems,
    subtotal: normalizedItems.reduce(
      (sum, item) => sum + finiteNumber(item.totalPrice ?? item.total_price),
      0,
    ),
  };
};

export const moveSplitEditorItem = (
  state,
  { invoiceId, item, quantity = 1, direction },
) => {
  const amount = Number(quantity);
  if (!Number.isInteger(amount) || amount <= 0) {
    return { ...state, changed: false, reason: "invalid-quantity" };
  }
  const invoices = Array.isArray(state?.invoices) ? state.invoices : [];
  const unassignedItems = mergeSplitEditorItems(state?.unassignedItems || []);
  const invoiceIndex = invoices.findIndex(
    (invoice) => String(invoice?.id) === String(invoiceId),
  );
  if (invoiceIndex < 0) {
    return { invoices, unassignedItems, changed: false, reason: "invoice-missing" };
  }
  const key = getSplitItemKey(item);
  const invoice = invoices[invoiceIndex];
  const invoiceItems = mergeSplitEditorItems(invoice?.items || []);

  if (direction === "add") {
    const availableIndex = unassignedItems.findIndex(
      (entry) => getSplitItemKey(entry) === key,
    );
    const available = unassignedItems[availableIndex];
    if (!available || finiteNumber(available.quantity) < amount) {
      return { invoices, unassignedItems, changed: false, reason: "unavailable" };
    }
    const selectedIndex = invoiceItems.findIndex(
      (entry) => getSplitItemKey(entry) === key,
    );
    const nextInvoiceItems = [...invoiceItems];
    if (selectedIndex >= 0) {
      nextInvoiceItems[selectedIndex] = withQuantity(
        nextInvoiceItems[selectedIndex],
        finiteNumber(nextInvoiceItems[selectedIndex].quantity) + amount,
      );
    } else {
      nextInvoiceItems.push(withQuantity(available, amount));
    }
    const remaining = finiteNumber(available.quantity) - amount;
    const nextUnassigned = [...unassignedItems];
    if (remaining > 0) nextUnassigned[availableIndex] = withQuantity(available, remaining);
    else nextUnassigned.splice(availableIndex, 1);
    const nextInvoices = [...invoices];
    nextInvoices[invoiceIndex] = recalculateInvoice(invoice, nextInvoiceItems);
    return {
      invoices: nextInvoices,
      unassignedItems: nextUnassigned,
      changed: true,
    };
  }

  if (direction === "remove") {
    const selectedIndex = invoiceItems.findIndex(
      (entry) => getSplitItemKey(entry) === key,
    );
    const selected = invoiceItems[selectedIndex];
    if (!selected || finiteNumber(selected.quantity) < amount) {
      return { invoices, unassignedItems, changed: false, reason: "not-selected" };
    }
    const remaining = finiteNumber(selected.quantity) - amount;
    const nextInvoiceItems = [...invoiceItems];
    if (remaining > 0) nextInvoiceItems[selectedIndex] = withQuantity(selected, remaining);
    else nextInvoiceItems.splice(selectedIndex, 1);
    const returned = withQuantity(selected, amount);
    const nextInvoices = [...invoices];
    nextInvoices[invoiceIndex] = recalculateInvoice(invoice, nextInvoiceItems);
    return {
      invoices: nextInvoices,
      unassignedItems: mergeSplitEditorItems([...unassignedItems, returned]),
      changed: true,
    };
  }

  return { invoices, unassignedItems, changed: false, reason: "invalid-direction" };
};

export const moveSplitEditorNatureItem = (
  state,
  { invoiceId, natureKey, quantity = 1, direction },
) => {
  const amount = Number(quantity);
  if (!Number.isInteger(amount) || amount <= 0) {
    return { ...state, changed: false, reason: "invalid-quantity" };
  }

  let nextState = state;
  let moved = 0;
  for (let index = 0; index < amount; index += 1) {
    const sourceItems =
      direction === "add"
        ? nextState?.unassignedItems || []
        : (nextState?.invoices || []).find(
            (invoice) => String(invoice?.id) === String(invoiceId),
          )?.items || [];
    const sourceItem =
      direction === "add"
        ? sourceItems.find(
            (item) =>
              getSplitItemNatureKey(item) === natureKey &&
              finiteNumber(item?.quantity) > 0,
          )
        : [...sourceItems].reverse().find(
            (item) =>
              getSplitItemNatureKey(item) === natureKey &&
              finiteNumber(item?.quantity) > 0,
          );
    if (!sourceItem) break;

    const result = moveSplitEditorItem(nextState, {
      invoiceId,
      item: sourceItem,
      quantity: 1,
      direction,
    });
    if (!result.changed) break;
    nextState = result;
    moved += 1;
  }

  return {
    ...nextState,
    changed: moved > 0,
    ...(moved > 0 ? {} : { reason: "nature-unavailable" }),
  };
};

export const buildSplitEditorSelectionRows = ({
  unassignedItems = [],
  selectedItems = [],
} = {}) => {
  const rows = new Map();
  let encounterIndex = 0;
  const include = (rawItem, bucket) => {
    const item = { ...rawItem };
    const key = getSplitItemNatureKey(item);
    const persistentId = Number(getPersistentSplitItemId(item));
    const orderRank = Number.isInteger(persistentId) && persistentId > 0
      ? persistentId
      : Number.MAX_SAFE_INTEGER;
    const row = rows.get(key) || {
      key,
      label: String(item?.name ?? item?.product_name ?? "Article"),
      availableItems: [],
      selectedItems: [],
      available: 0,
      selected: 0,
      unitTotal: getSplitItemUnitTotal(item),
      orderRank,
      encounterIndex,
    };
    row.orderRank = Math.min(row.orderRank, orderRank);
    const quantity = Math.max(0, finiteNumber(item.quantity));
    row[bucket].push(item);
    if (bucket === "availableItems") row.available += quantity;
    else row.selected += quantity;
    rows.set(key, row);
    encounterIndex += 1;
  };
  mergeSplitEditorItems(unassignedItems).forEach((item) =>
    include(item, "availableItems"),
  );
  mergeSplitEditorItems(selectedItems).forEach((item) =>
    include(item, "selectedItems"),
  );
  return Array.from(rows.values()).sort(
    (left, right) =>
      left.orderRank - right.orderRank ||
      left.encounterIndex - right.encounterIndex,
  );
};
